import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "../../icons/svg/outline";
import useOutsideClick from "../../../hooks/useOutsideClick";
import { itemMatchesSearchQuery } from "../../../utils/common/searchFilter";

/**
 * @typedef {{ value: string, label: string }} SearchableMultiSelectOption
 */

/**
 * Searchable multi-select with removable chips and an inline filterable option list.
 * Selecting an option adds a chip; chips and "Clear all" remove selections immediately.
 *
 * @param {Object} props
 * @param {string} [props.label] Optional field label shown above the control.
 * @param {SearchableMultiSelectOption[]} props.options All selectable options.
 * @param {string[]} props.value Currently selected option values.
 * @param {(values: string[]) => void} props.onChange Called when the selection changes.
 * @param {string} [props.placeholder="Search..."] Input placeholder when the list is open or empty.
 * @param {string} [props.emptyPlaceholder="All"] Shown in the input area when nothing is selected and closed.
 * @param {string} [props.clearAllLabel="Clear all"] Label for the clear-all control.
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.className=""]
 * @param {string} [props.listboxClassName="z-[9999]"] Z-index/extra classes for the portaled listbox.
 * @param {string} [props.ariaLabel] Accessible name when `label` is omitted.
 * @returns {JSX.Element}
 */
export default function SearchableMultiSelect({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = "Search...",
  emptyPlaceholder = "All",
  clearAllLabel = "Clear all",
  disabled = false,
  className = "",
  listboxClassName = "z-[9999]",
  ariaLabel,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [keyboardIndex, setKeyboardIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listboxId = useId();
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  // Selected values normalized to strings to match option-value comparisons.
  const selectedSet = useMemo(() => new Set(value.map(String)), [value]);

  const optionByValue = useMemo(() => {
    const map = new Map();
    for (const option of options) {
      map.set(String(option.value), option);
    }
    return map;
  }, [options]);

  const selectedOptions = useMemo(
    () =>
      value
        .map((selectedValue) => optionByValue.get(String(selectedValue)))
        .filter(Boolean),
    [value, optionByValue]
  );

  // Unselected options that match the current search term (single pass).
  const filteredOptions = useMemo(
    () =>
      options.filter(
        (option) =>
          !selectedSet.has(String(option.value)) &&
          itemMatchesSearchQuery(option, searchTerm, ["label"])
      ),
    [options, selectedSet, searchTerm]
  );

  const closeList = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
    setKeyboardIndex(0);
  }, []);

  const openList = useCallback(() => {
    if (disabled) {
      return;
    }
    setIsOpen(true);
    setKeyboardIndex(0);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [disabled]);

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) {
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useOutsideClick(containerRef, closeList, {
    ignoreSelector: "[data-searchable-multi-select-listbox]",
  });

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (!isOpen || !listRef.current) {
      return;
    }
    const item = listRef.current.children[keyboardIndex];
    item?.scrollIntoView({ block: "nearest" });
  }, [keyboardIndex, isOpen]);

  useEffect(() => {
    setKeyboardIndex(0);
  }, [searchTerm]);

  /**
   * @param {string} optionValue
   */
  const addOption = useCallback(
    (optionValue) => {
      const key = String(optionValue);
      if (selectedSet.has(key)) {
        return;
      }
      onChange([...value, key]);
      setSearchTerm("");
      setKeyboardIndex(0);
      inputRef.current?.focus();
    },
    [onChange, selectedSet, value]
  );

  /**
   * @param {string} optionValue
   */
  const removeOption = useCallback(
    (optionValue) => {
      const key = String(optionValue);
      onChange(value.filter((entry) => String(entry) !== key));
    },
    [onChange, value]
  );

  const handleClearAll = useCallback(() => {
    onChange([]);
    setSearchTerm("");
    setKeyboardIndex(0);
    inputRef.current?.focus();
  }, [onChange]);

  /**
   * Move the keyboard highlight by `delta`, clamped to the visible option range.
   * @param {number} delta Positive moves down, negative moves up.
   */
  const moveKeyboardIndex = (delta) => {
    const lastIndex = filteredOptions.length - 1;
    setKeyboardIndex((index) =>
      Math.min(Math.max(index + delta, 0), Math.max(lastIndex, 0))
    );
  };

  /**
   * @param {import('react').KeyboardEvent<HTMLInputElement>} event
   */
  const handleInputKeyDown = (event) => {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault();
        // First press opens the list; subsequent presses move the highlight.
        if (!isOpen) {
          openList();
        } else {
          moveKeyboardIndex(event.key === "ArrowDown" ? 1 : -1);
        }
        break;
      case "Enter": {
        event.preventDefault();
        if (!isOpen) {
          openList();
          break;
        }
        const option = filteredOptions[keyboardIndex];
        if (option) {
          addOption(option.value);
        }
        break;
      }
      case "Escape":
        event.preventDefault();
        closeList();
        inputRef.current?.blur();
        break;
      case "Backspace":
        // Empty input + Backspace removes the most recent chip.
        if (!searchTerm && value.length > 0) {
          onChange(value.slice(0, -1));
        }
        break;
      default:
        break;
    }
  };

  const controlAriaLabel = ariaLabel ?? label ?? "Searchable multi-select";
  const showClearAll = value.length > 0 && !disabled;
  const inputPlaceholder = value.length > 0
    ? (isOpen ? placeholder : "")
    : (isOpen ? placeholder : emptyPlaceholder);

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label || showClearAll ? (
        <div className="flex items-center justify-between gap-2">
          {label ? (
            <span className="text-xs font-medium text-gray-600">{label}</span>
          ) : (
            <span />
          )}
          {showClearAll ? (
            <button
              type="button"
              className="text-xs font-medium text-gray-500 hover:text-gray-800"
              onClick={handleClearAll}
            >
              {clearAllLabel}
            </button>
          ) : null}
        </div>
      ) : null}

      <div ref={containerRef} className="relative">
        <div
          className={`flex min-h-[2.25rem] flex-wrap items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 ${
            disabled
              ? "cursor-not-allowed bg-gray-50 opacity-60"
              : "cursor-text focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500"
          }`}
          onClick={() => {
            if (!disabled) {
              openList();
            }
          }}
        >
          {selectedOptions.map((option) => (
            <span
              key={option.value}
              className="inline-flex max-w-full items-center gap-1 rounded bg-teal-600 px-2 py-0.5 text-xs font-medium text-white"
            >
              <span className="truncate">{option.label}</span>
              {!disabled ? (
                <button
                  type="button"
                  className="shrink-0 rounded hover:bg-teal-900 p-[1px]"
                  aria-label={`Remove ${option.label}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    removeOption(option.value);
                  }}
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              ) : null}
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            value={searchTerm}
            disabled={disabled}
            placeholder={inputPlaceholder}
            aria-label={controlAriaLabel}
            aria-expanded={isOpen}
            aria-controls={isOpen ? listboxId : undefined}
            className="min-w-[4rem] flex-1 border-0 bg-transparent py-1 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed"
            onFocus={openList}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              if (!isOpen) {
                setIsOpen(true);
              }
            }}
            onKeyDown={handleInputKeyDown}
          />
        </div>

        {isOpen && !disabled
          ? createPortal(
            <ul
              id={listboxId}
              ref={listRef}
              role="listbox"
              aria-label={`${controlAriaLabel} options`}
              data-searchable-multi-select-listbox={listboxId}
              className={`fixed font-inter max-h-60 overflow-y-auto rounded-md border border-gray-300 bg-white py-1 shadow-lg ${listboxClassName}`}
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
              }}
            >
              {filteredOptions.length === 0 ? (
                <li className="px-3 py-2 text-xs text-gray-500">
                  No options found
                </li>
              ) : (
                filteredOptions.map((option, index) => (
                  <li key={option.value} role="option" aria-selected={false}>
                    <button
                      type="button"
                      className={`w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 ${
                        index === keyboardIndex ? "bg-gray-100" : ""
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => addOption(option.value)}
                      onMouseEnter={() => setKeyboardIndex(index)}
                    >
                      {option.label}
                    </button>
                  </li>
                ))
              )}
            </ul>,
            document.body
          )
          : null}
      </div>
    </div>
  );
}
